// Phone number utility functions

/**
 * Convert any phone number format to E.164 format for Firebase verification
 * @param {string} phoneNumber - The phone number in any format
 * @returns {string} - E.164 formatted phone number (e.g., +1234567890)
 */
export const toE164Format = (phoneNumber) => {
  if (!phoneNumber) return null;
  
  // Remove all non-digit characters except +
  let cleaned = phoneNumber.replace(/[^\d+]/g, '');
  
  // If already in E.164 format, return as is
  if (cleaned.startsWith('+')) {
    return cleaned;
  }
  
  // Remove any leading zeros
  cleaned = cleaned.replace(/^0+/, '');
  
  // Handle different length scenarios
  if (cleaned.length === 9) {
    // For 9-digit numbers, add +1 and a leading 0 to make it valid
    // This converts 123456789 to +10123456789 (valid format)
    return '+1' + '0' + cleaned;
  } else if (cleaned.length === 10) {
    // If it's a 10-digit number, assume US (+1)
    return '+1' + cleaned;
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    // If it's 11 digits starting with 1, add +
    return '+' + cleaned;
  } else if (cleaned.length < 9) {
    // For very short numbers, pad with zeros to make them valid
    const padded = cleaned.padEnd(9, '0');
    return '+1' + '0' + padded;
  } else {
    // For other cases, add +1 as default
    return '+1' + cleaned;
  }
};

/**
 * Format phone number for display (user-friendly format)
 * @param {string} phoneNumber - The phone number in any format
 * @returns {string} - Formatted phone number for display
 */
export const formatForDisplay = (phoneNumber) => {
  console.log('formatForDisplay - input:', phoneNumber, 'type:', typeof phoneNumber);
  if (!phoneNumber) return '';
  
  // If it's already in display format (has dashes), return as is
  if (phoneNumber.includes('-')) {
    console.log('formatForDisplay - already formatted, returning:', phoneNumber);
    return phoneNumber;
  }
  
  // Convert to E.164 first
  const e164 = toE164Format(phoneNumber);
  if (!e164) return phoneNumber; // Return original if conversion fails
  
  // Remove the + and country code for US numbers
  if (e164.startsWith('+1')) {
    const digits = e164.substring(2);
    
    if (digits.length === 9) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 9)}`;
    } else if (digits.length === 10) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    } else if (digits.length === 11) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}-${digits.slice(10, 11)}`;
    } else {
      return digits;
    }
  }
  
  // For international numbers, return as is
  return e164;
};

/**
 * Validate phone number format
 * @param {string} phoneNumber - The phone number to validate
 * @returns {object} - Validation result with isValid, formatted, and error properties
 */
export const validatePhoneNumber = (phoneNumber) => {
  if (!phoneNumber) {
    return { 
      isValid: false, 
      formatted: null, 
      error: "Phone number is required" 
    };
  }
  
  const formatted = toE164Format(phoneNumber);
  
  if (!formatted) {
    return { 
      isValid: false, 
      formatted: null, 
      error: "Invalid phone number format" 
    };
  }
  
  // Basic validation for international phone number (allow 9-15 digits total)
  const phoneRegex = /^\+[1-9]\d{8,14}$/;
  const isValid = phoneRegex.test(formatted);
  
  if (!isValid) {
    return { 
      isValid: false, 
      formatted: null, 
      error: "Invalid phone number format. Please use international format (e.g., +1234567890)" 
    };
  }
  
  return { isValid: true, formatted, error: null };
};

/**
 * Check if phone number is in E.164 format
 * @param {string} phoneNumber - The phone number to check
 * @returns {boolean} - True if in E.164 format
 */
export const isE164Format = (phoneNumber) => {
  if (!phoneNumber) return false;
  return /^\+[1-9]\d{8,14}$/.test(phoneNumber);
}; 